import { t } from '../i18n';
import type { District, WorldMap } from '../api/types';

/**
 * PUBG-style map screen (Blueprint DR-02): full city map → pick a drop point.
 * First visit to a district is a walk (initial pick, or an unvisited point while
 * traveling just hints "walk there first"); a visited district fast-travels in one tap.
 */

const SVG_NS = 'http://www.w3.org/2000/svg';

export type MapMode = 'initial' | 'travel';

export class MapScreen {
  private overlay = document.getElementById('map-screen')!;
  private titleEl = document.getElementById('map-title')!;
  private hintEl = document.getElementById('map-hint')!;
  private closeBtn = document.getElementById('map-close') as HTMLButtonElement;
  private svgHost = document.getElementById('map-svg-host')!;

  private readonly visited = new Set<string>();
  private readonly pointEls = new Map<string, SVGGElement>();
  private mode: MapMode = 'initial';
  private onPick: ((x: number, z: number) => void) | null = null;

  constructor(map: WorldMap) {
    this.closeBtn.textContent = t('close');
    this.closeBtn.addEventListener('click', () => this.close());
    this.svgHost.appendChild(this.buildSvg(map));
  }

  get isOpen(): boolean {
    return this.overlay.style.display === 'flex';
  }

  get visitedCount(): number {
    return this.visited.size;
  }

  /** Shows the map. `onPick` fires once a valid point is chosen (map closes itself). */
  open(mode: MapMode, onPick: (x: number, z: number) => void): void {
    this.mode = mode;
    this.onPick = onPick;
    this.titleEl.textContent = mode === 'initial' ? t('map_title_initial') : t('map_title_travel');
    this.closeBtn.style.display = mode === 'travel' ? 'inline-block' : 'none';
    this.hintEl.textContent = mode === 'travel' ? t('map_hint_visited') : '';
    this.updatePointStyles();
    this.overlay.style.display = 'flex';
  }

  close(): void {
    this.overlay.style.display = 'none';
  }

  markVisited(districtId: string): void {
    if (this.visited.has(districtId)) return;
    this.visited.add(districtId);
    this.updatePointStyles();
  }

  private pick(district: District): void {
    if (this.mode === 'travel' && !this.visited.has(district.id)) {
      this.hintEl.textContent = t('map_hint_unvisited');
      return;
    }
    const point = dropPointFor(district);
    this.close();
    this.onPick?.(point.x, point.z);
  }

  private updatePointStyles(): void {
    for (const [id, g] of this.pointEls) {
      const visited = this.visited.has(id);
      g.classList.toggle('visited', visited);
      g.classList.toggle('locked-travel', this.mode === 'travel' && !visited);
    }
  }

  private buildSvg(map: WorldMap): SVGSVGElement {
    const half = map.worldHalf;
    const svg = document.createElementNS(SVG_NS, 'svg') as unknown as SVGSVGElement;
    svg.setAttribute('viewBox', `${-half} ${-half} ${half * 2} ${half * 2}`);
    svg.setAttribute('class', 'map-svg');

    for (const road of map.roads) {
      const line = document.createElementNS(SVG_NS, 'line');
      line.setAttribute('x1', String(road.from.x));
      line.setAttribute('y1', String(road.from.z));
      line.setAttribute('x2', String(road.to.x));
      line.setAttribute('y2', String(road.to.z));
      line.setAttribute('class', 'map-road');
      line.setAttribute('stroke-width', String(road.width * 0.4));
      svg.appendChild(line);
    }

    for (const district of map.districts) {
      const g = document.createElementNS(SVG_NS, 'g');
      g.setAttribute('class', 'map-district');
      g.setAttribute('id', `map-point-${district.id}`);
      g.setAttribute('data-district-id', district.id);

      const circle = document.createElementNS(SVG_NS, 'circle');
      circle.setAttribute('cx', String(district.center.x));
      circle.setAttribute('cy', String(district.center.z));
      circle.setAttribute('r', String(district.radius));
      circle.setAttribute('fill', district.color);
      circle.setAttribute('class', 'map-district-circle');
      g.appendChild(circle);

      const label = document.createElementNS(SVG_NS, 'text');
      label.setAttribute('x', String(district.center.x));
      label.setAttribute('y', String(district.center.z));
      label.setAttribute('class', 'map-district-label');
      label.textContent = district.nameAr;
      g.appendChild(label);

      g.addEventListener('click', () => this.pick(district));
      svg.appendChild(g);
      this.pointEls.set(district.id, g);
    }

    for (const building of map.buildings) {
      const dot = document.createElementNS(SVG_NS, 'circle');
      dot.setAttribute('cx', String(building.position.x));
      dot.setAttribute('cy', String(building.position.z));
      dot.setAttribute('r', '3');
      dot.setAttribute('class', building.locked ? 'map-building-locked' : 'map-building');
      svg.appendChild(dot);
    }

    for (const slot of map.houseSlots) {
      const dot = document.createElementNS(SVG_NS, 'circle');
      dot.setAttribute('cx', String(slot.position.x));
      dot.setAttribute('cy', String(slot.position.z));
      dot.setAttribute('r', '2');
      dot.setAttribute('class', 'map-house');
      svg.appendChild(dot);
    }

    return svg;
  }
}

/** A safe point inside the district, pulled toward the city center so it never lands inside a building. */
function dropPointFor(district: District): { x: number; z: number } {
  const dist = Math.hypot(district.center.x, district.center.z);
  if (dist < 1) {
    return { x: 0, z: 24 }; // the city-center district — reuse the original default spawn
  }
  const pull = Math.min(0.5, (district.radius * 0.5) / dist);
  return {
    x: district.center.x * (1 - pull),
    z: district.center.z * (1 - pull),
  };
}
